/* global twemoji */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from './store/store';
import { useMutation, useQuery } from 'react-query';
import { getDocumentTypes } from './requests/documentTypes';
import { getProposalSections, getTemplateSections } from './requests/sections';
import { getOrganizationalCharts } from './requests/organizationalCharts';
import emojiToolkit from 'emoji-toolkit';
import {
  createCustomTextVariable,
  updateCustomTextVariable,
} from './requests/customTextVariable';
import {
  generateColorsObject,
  generateFamilyObject,
  generateFontSizesObject,
  generateHighlightObject,
  ItemTypes,
  MASKS_TYPE,
  viewerTypes,
} from './constants/constants';
import {
  createVariableGroup,
  updateVariableGroup,
} from './requests/variableGroup';
import { CopyBox, CopyContainer, isAImgBox, isASelectionBox, isATableBox, isAVariableBox } from './utils/boxes';
import { getContainerColumnIds, getTopLevelContainer } from './utils/columns';
import { OverlayContext, leftShownStatus } from './contexts/OverlayContext';
import { shouldHideSectionVariable } from './utils/sections';
import { filterChapterSeparationContainers } from './utils/containers';
import { App, Button, message } from 'antd';
import { GLOSSARY } from './constants/constants';
import {
  mergeConfigurationColors,
  mergeConfigurationHighlightColors,
} from './utils/styles';
import { RichUtils, Modifier, EditorState } from 'draft-js';
import { getDefaultKeyBinding } from 'draft-js';
import shallow from 'zustand/shallow';
import { savePagePreview } from './utils/savePagePreview';
import { useTranslation } from 'react-i18next';

export const useOutsideAlerter = (ref, stateChange, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;
    function handleClickOutside(event) {
      // Ignorer les clics explicitement marqués comme ne devant pas fermer les menus
      const ignoreTarget = event.target?.closest?.(
        '[data-outside-ignore="true"]'
      );
      const insideIgnoreTarget = event.target?.closest?.(
        '[data-inside-ignore="false"]'
      );
      if (ignoreTarget && !insideIgnoreTarget) {
        return;
      }

      if (ref.current && !ref.current.contains(event.target)) {
        stateChange(false);
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, stateChange, enabled]);
};

// Menu flottant courant, tous consommateurs confondus.
let closeOpenFloatingMenu = null;

/**
 * Ferme le menu flottant déjà ouvert quand celui-ci s'ouvre : un seul à la fois.
 *
 * Chaque menu porte son propre état local et son propre useOutsideAlerter, qui
 * ignore par construction les clics marqués `data-outside-ignore` — donc les
 * clics sur un autre menu. Sans arbitre commun, ouvrir le second laissait le
 * premier affiché, les deux se chevauchant. À appeler depuis le menu lui-même,
 * avec la fonction de fermeture que lui passe son propriétaire.
 */
export const useSingleOpenFloatingMenu = (closeMenu) => {
  useEffect(() => {
    closeOpenFloatingMenu?.();
    closeOpenFloatingMenu = closeMenu;
    // Le menu suivant s'enregistre avant que celui-ci ne soit démonté (React
    // ferme l'ancien via un setState, dont le démontage n'arrive qu'au rendu
    // d'après) : ne rendre la place que si on l'occupe encore.
    return () => {
      if (closeOpenFloatingMenu === closeMenu) closeOpenFloatingMenu = null;
    };
  }, [closeMenu]);
};

const selector = (state) => ({
  setEventManagerEnabled: state.setEventManagerEnabled,
});

export const useSaveDocument = () => {
  const [saveDocumentLoading, setSaveDocumentLoading] = useState(false);
  const handleUnSelectBox = useStore((state) => state.handleUnSelectBox);
  const handleUnSelectContainer = useStore(
    (state) => state.handleUnSelectContainer
  );
  const isAdmin = useStore((state) => state?.user?.isAdmin);
  const currentDocument = useStore((state) => state.currentDocument);
  const currentProposal = useStore((state) => state.currentProposal);
  const updateTemplate = useStore((state) => state.updateTemplate);
  const saveProposal = useStore((state) => state.saveProposal);
  const landscape = useStore((state) => state.landscape);

  const saveDocument = useCallback(
    async (onFinish) => {
      handleUnSelectBox();
      handleUnSelectContainer();
      const key = 'saveDocument';
      message.open({
        type: 'loading',
        content: 'Enregistrement en cours',
        duration: 0,
        key,
      });
      setSaveDocumentLoading(true);
      setTimeout(async () => {
        if (isAdmin && currentDocument.type === 'template') {
          try {
            await updateTemplate();
            savePagePreview(currentDocument.id, currentDocument.type, landscape);
            message.open({
              type: 'success',
              content: 'Enregistré.',
              duration: 1,
              key,
            });
          } catch (error) {
            message.open({
              type: 'error',
              content: "Une erreur est survenue lors de l'enregistrement.",
              duration: 2,
              key,
            });
          }
        } else {
          try {
            await saveProposal();
            savePagePreview(currentProposal.id, currentDocument.type, landscape);
            message.open({
              type: 'success',
              content: 'Enregistré.',
              duration: 1,
              key,
            });
          } catch (error) {
            message.open({
              type: 'error',
              content: "Une erreur est survenue lors de l'enregistrement.",
              duration: 2,
              key,
            });
          }
        }
        setSaveDocumentLoading(false);
        if (onFinish) onFinish();
      }, 0);
    },
    [
      isAdmin,
      currentDocument?.type,
      currentDocument?.id,
      updateTemplate,
      landscape,
      saveProposal,
      currentProposal?.id,
      handleUnSelectBox,
      handleUnSelectContainer,
    ]
  );

  return { saveDocument, saveDocumentLoading };
};

export const useModalOpener = (defaultValue) => {
  const [isOpen, setIsOpen] = useState(defaultValue);
  const { setEventManagerEnabled } = useStore(selector);

  const toggleModal = (opened) => {
    setIsOpen(opened);
    setEventManagerEnabled(!opened);
  };

  useEffect(() => {
    // Réactiver l'eventManagerEnabled quand la modale est fermée
    if (!isOpen) {
      setEventManagerEnabled(true);
    }
  }, [isOpen, setEventManagerEnabled]);

  return [isOpen, toggleModal];
};

export const useKeyboard = (defaultValue) => {
  const { setEventManagerEnabled } = useStore(selector);

  const setIsUsingKeyboard = (isUsing) => {
    setEventManagerEnabled(!isUsing);
  };

  return setIsUsingKeyboard;
};

export const useUpdateBoxOnUnselect = ({ boxId, isSelected, updateBox }) => {
  const wasLastSelected = useRef(false);
  useEffect(() => {
    if (isSelected) {
      wasLastSelected.current = true;
    }
    if (!isSelected && wasLastSelected.current === true && boxId) {
      wasLastSelected.current = false;
      updateBox(boxId);
    }
  }, [boxId, isSelected, updateBox]);
};

export const useQueryDocumentTypes = (options = {}) =>
  useQuery(['DocumentTypes'], getDocumentTypes, {
    refetchOnWindowFocus: false,
    placeholderData: [],
    ...options,
  });

export const useQueryDocumentSections = (
  { documentType, documentId },
  options = {}
) => {
  const isDocumentLoading = useStore((state) => state.isDocumentLoading);
  const fetchFn =
    documentType === 'template' ? getTemplateSections : getProposalSections;
  return useQuery(
    ['Sections', { documentType, documentId }],
    () => fetchFn(documentId),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      enabled: !!documentId && !!documentType && !isDocumentLoading,
      ...options,
    }
  );
};

export const useOrganizationalCharts = ({ proposalId, documentType }, options = {}) =>
  useQuery(
    ['OrganizationalCharts', { proposalId }],
    () => getOrganizationalCharts(proposalId),
    {
      refetchOnWindowFocus: false,
      enabled: !!proposalId && documentType !== 'template',
      ...options,
    }
  );

export const useCreateCustomTextVariable = (options = {}) => {
  return useMutation(
    ({ name, remarks }) => createCustomTextVariable({ name, remarks }),
    {
      ...options,
      onError: (err) => {
        if (err.response?.data.name) {
          message.error({
            content: 'Ce nom de variable texte a déjà été utilisé.',
            key: 422,
          });
        }
      },
    }
  );
};

export const useUpdateCustomTextVariable = (options = {}) => {
  return useMutation(
    ({ name, id, remarks, predefinedValues }) =>
      updateCustomTextVariable({ id, name, remarks, predefinedValues }),
    {
      ...options,
      onError: (err) => {
        if (err.response?.data.name) {
          message.error({
            content: 'Ce nom de variable texte a déjà été utilisé.',
            key: 422,
          });
        }
      },
    }
  );
};

export const useCreateVariableGroup = (options = {}) => {
  return useMutation(
    ({ name, remark, disposition }) =>
      createVariableGroup({ name, remark, disposition }),
    {
      ...options,
    }
  );
};

export const useUpdateVariableGroup = (options = {}) => {
  return useMutation(
    ({ name, remark, variableIds, id, disposition }) =>
      updateVariableGroup({
        name,
        remark,
        variableIds,
        id,
        disposition,
      }),
    {
      ...options,
    }
  );
};

export const useStyleMap = () => {
  const { configuration, tmp } = useStore((state) => ({
    configuration: state.configuration,
    tmp: state?.tmp,
  }));

  const fromPdf = useStore((state) => state.fromPdf);
  const createStyleMap = useCallback(
    () => ({
      CODE: {
        backgroundColor: 'rgba(0, 0, 0, 1.0)',
        fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
        fontSize: 16,
        padding: 2,
      },
      BOLD: {
        fontWeight: 1000,
      },
      HIGHLIGHT: {
        backgroundColor: configuration.currentHighlightColor.style,
      },
      HIGHLIGHT_green: {
        backgroundColor: 'rgba(12, 180, 90, 0.35)',
      },
      ...generateHighlightObject(
        mergeConfigurationHighlightColors(configuration, tmp, false)
      ),
      ...generateFontSizesObject(),
      ...generateColorsObject(
        mergeConfigurationColors(configuration, tmp, false)
      ),
      ...generateFamilyObject(configuration.fontFamilyList, fromPdf),
    }),
    [configuration, fromPdf, tmp]
  );

  const initStyleMap = useMemo(() => {
    return createStyleMap();
  }, [createStyleMap]);

  const [styleMap, setStyleMap] = useState(initStyleMap);

  useEffect(() => {
    setStyleMap(createStyleMap);
  }, [createStyleMap]);
  return styleMap;
};

export const useMenuPosition = (ref) => {
  const [menuPosition, setMenuPosition] = useState({ top: null, left: null });

  useEffect(() => {
    const updateTopPosition = () => {
      if (ref.current) {
        const { top, left } = ref.current.getBoundingClientRect();
        setMenuPosition({ top: top - 50, left: left + 200 });
      }
    };

    // Initial top position update
    updateTopPosition();

    // Create a ResizeObserver to watch for changes
    const observer = new ResizeObserver(updateTopPosition);
    observer.observe(ref.current);

    // Optionally, you can also track scrolling
    window.addEventListener('scroll', updateTopPosition);

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateTopPosition);
    };
  }, [ref]);

  return menuPosition;
};

export { useOverlayScrollbarThumb } from './components/common/useOverlayScrollbarThumb';

export const useHandleBoxSelect = ({
  fromViewer,
  box,
  isSelected,
  drawMode,
}) => {
  const currentProposal = useStore(({ currentProposal }) => currentProposal);
  const handleSelectBox = useStore(({ handleSelectBox }) => handleSelectBox);
  const container = useStore(({ containers }) =>
    containers.find((container) => container.columnsIds.includes(box.columnId))
  );
  const maskType = useStore(
    ({ masks }) =>
      masks.find((m) => m.id === MASKS_TYPE.HEADER.id)?.editableMask
  );
  const maskTypeEditable = typeof maskType !== 'boolean' ? true : maskType;
  return useCallback(
    (event) => {
      const group = event.ctrlKey || event.metaKey;
      const range = event.shiftKey;
      if (
        fromViewer === viewerTypes.PAGENUMBER ||
        fromViewer === viewerTypes.DMZ ||
        fromViewer === viewerTypes.PREVIEW
      )
        return;
      if (
        (box.maskId === 'header' || box.maskId === 'footer') &&
        !maskTypeEditable &&
        box.type !== 'img'
      ) {
        return null;
      }
      if (currentProposal.id === null && container?.from_linked_section)
        return null;
      if (group || range) {
        if (!drawMode) return null;
        const { selectedBoxId: curBoxId, boxes, masks } =
          useStore.getState();
        const allBoxes = [...boxes, ...masks.map((m) => m.boxes).flat()];
        const curBox = curBoxId
          ? allBoxes.find((b) => b.id === curBoxId)
          : null;
        const sameMask =
          !!box?.maskId && !!curBox?.maskId && box.maskId === curBox.maskId;
        const sameContainer =
          !!container?.columnsIds?.length &&
          !!curBox?.columnId &&
          container.columnsIds.includes(curBox.columnId);
        const canMultiSelect = !!curBox && (sameMask || sameContainer);
        if (!canMultiSelect)
          return null;
      }
      if (
        container?.editable ||
        !container?.hasOwnProperty('editable') ||
        currentProposal.id === null
      ) {
        if (!isSelected) {
          handleSelectBox({ box, group, fromViewer });
        }
      } else {
        if (box.type === 'img' || isAVariableBox(box.type)) {
          if (!isSelected) {
            handleSelectBox({ box, group, fromViewer });
          }
        }
      }
    },
    [
      fromViewer,
      box,
      maskTypeEditable,
      currentProposal.id,
      container,
      isSelected,
      handleSelectBox,
      drawMode,
    ]
  );
};

export const useContainersToShow = () => {
  // Utiliser un seul selector au lieu de 5 appels séparés pour réduire les re-renders
  const { boxes, columns, containers, documentId, documentType, masks, displayChapterSeparation } = useStore(
    ({ boxes, columns, containers, currentDocument, currentProposal, masks, configuration }) => ({
      boxes,
      columns,
      containers,
      documentId:
        currentDocument.type === 'template'
          ? currentDocument.id
          : currentProposal.id,
      documentType: currentDocument.type,
      masks,
      displayChapterSeparation: configuration.displayChapterSeparation ?? true,
    }),
    shallow
  );

  const { data: sectionList } = useQueryDocumentSections({
    documentType,
    documentId,
  });

  const containersToShow = useMemo(() => {
    // Map columnId -> box type pour les boxes à masquer
    const hiddenBoxTypeByColumnId = new Map();
    for (let i = 0; i < boxes.length; i++) {
      const b = boxes[i];
      if (
        b.hideField ||
        b.type === ItemTypes.SECTION_LINK ||
        (documentType !== 'template' &&
          shouldHideSectionVariable({
            type: b.type,
            sectionId: b.sectionId,
            sectionList,
            isActivatedByTrigger: b.isActivatedByTrigger,
          }))
      ) {
        hiddenBoxTypeByColumnId.set(b.columnId, b.type);
      }
    }

    // Set des colonnes à masquer (lookup O(1) au lieu de scan d'array)
    const columnsToHideIds = new Set();
    for (let i = 0; i < columns.length; i++) {
      const { id, drawMode } = columns[i];
      if (
        hiddenBoxTypeByColumnId.has(id) &&
        (!drawMode || isASelectionBox(hiddenBoxTypeByColumnId.get(id)))
      ) {
        columnsToHideIds.add(id);
      }
    }

    const chapterFiltered = filterChapterSeparationContainers({
      containers,
      boxes,
      columns,
      masks,
      displayChapterSeparation,
    });

    // Filtrer les containers : garder ceux dont au moins une colonne est visible
    return chapterFiltered.filter((container) => {
      if (container.columnsIds.every((id) => columnsToHideIds.has(id))) return false;
      return true;
    });
  }, [boxes, columns, containers, documentType, sectionList, masks, displayChapterSeparation]);

  return containersToShow;
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

export const useBulletDraftJs = () => {
  const configuration = useStore(({ configuration }) => configuration);
  useEffect(() => {
    var root = document.querySelector(':root');
    function getElement(element, defaultElement) {
      const escapedFind = emojiToolkit.escapeRegExp(
        emojiToolkit.unicodeCharRegex()
      );
      const elementToUse = element === 'Defaut' ? defaultElement : element;
      const search = new RegExp(`(${escapedFind})`, 'gi');
      if (search.exec(elementToUse) === null) {
        return elementToUse?.length === 3 ? elementToUse : `"${elementToUse}"`;
      }
      // twemoji est chargé de façon asynchrone depuis index.html : au tout
      // premier passage il peut ne pas être là. On retombe alors sur le
      // caractère brut, comme lorsque la conversion ne produit pas d'image.
      if (typeof twemoji === 'undefined') {
        return elementToUse?.length === 3 ? elementToUse : `"${elementToUse}"`;
      }
      const twemojiElem = twemoji.parse(elementToUse, {
        folder: 'svg',
        ext: '.svg',
        base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/',
      });
      // Create a temporary DOM element to extract the URL
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = twemojiElem;

      // Find the image element and extract the `src` attribute
      const twemojiImg = tempDiv.querySelector('img'); // Find the first img element
      const emojiUrl = twemojiImg
        ? `url(${twemojiImg.src})`
        : elementToUse?.length === 3
          ? elementToUse
          : `"${elementToUse}"`;

      return emojiUrl;
    }

    var element0 = getElement(configuration.unorderedListBulletValue0, "'•'");
    var element1 = getElement(configuration.unorderedListBulletValue1, "'◦'");
    var element2 = getElement(configuration.unorderedListBulletValue2, "'▪'");
    var element3 = getElement(configuration.unorderedListBulletValue3, "'▪'");
    var element4 = getElement(configuration.unorderedListBulletValue4, "'▪'");
    var customBulletPadding = configuration.customBulletPadding;
    const getBulletColor = (bulletColorConfig) =>
      bulletColorConfig?.label === 'transparent' || !bulletColorConfig?.style
        ? 'inherit'
        : bulletColorConfig.style;
    const bulletColor0 = getBulletColor(configuration.unorderedListBulletColor0);
    const bulletColor1 = getBulletColor(configuration.unorderedListBulletColor1);
    const bulletColor2 = getBulletColor(configuration.unorderedListBulletColor2);
    const bulletColor3 = getBulletColor(configuration.unorderedListBulletColor3);
    const bulletColor4 = getBulletColor(configuration.unorderedListBulletColor4);

    root.style.setProperty('--unorderedSymbol0', element0);
    root.style.setProperty('--unorderedSymbol1', element1);
    root.style.setProperty('--unorderedSymbol2', element2);
    root.style.setProperty('--unorderedSymbol3', element3);
    root.style.setProperty('--unorderedSymbol4', element4);
    root.style.setProperty('--unorderedSymbolColor0', bulletColor0);
    root.style.setProperty('--unorderedSymbolColor1', bulletColor1);
    root.style.setProperty('--unorderedSymbolColor2', bulletColor2);
    root.style.setProperty('--unorderedSymbolColor3', bulletColor3);
    root.style.setProperty('--unorderedSymbolColor4', bulletColor4);
    root.style.setProperty('--customBulletPaddingCSS', customBulletPadding);
  }, [
    configuration.unorderedListBulletValue,
    configuration.unorderedListBulletValue0,
    configuration.unorderedListBulletValue1,
    configuration.unorderedListBulletValue2,
    configuration.unorderedListBulletValue3,
    configuration.unorderedListBulletValue4,
    configuration.unorderedListBulletColor0,
    configuration.unorderedListBulletColor1,
    configuration.unorderedListBulletColor2,
    configuration.unorderedListBulletColor3,
    configuration.unorderedListBulletColor4,
    configuration.customBulletPadding,
  ]);
};

export const useTabHandler = (editorState, setEditorState) => {
  const handleTab = useCallback(
    (e) => {
      e.preventDefault();
      const selection = editorState.getSelection();
      const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
      const currentContent = editorState.getCurrentContent();

      if (
        blockType === 'unordered-list-item' ||
        blockType === 'ordered-list-item'
      ) {
        const newState = RichUtils.onTab(e, editorState, 4);
        if (newState) {
          setEditorState(newState);
          return 'handled';
        } else {
          return 'not-handled';
        }
      } else {
        // Utiliser la sélection actuelle au lieu du dernier bloc
        const textWithInsert = Modifier.insertText(
          currentContent,
          selection,
          '    ',
          null
        );
        const editorWithInsert = EditorState.push(
          editorState,
          textWithInsert,
          'insert-characters'
        );
        const newEditorState = EditorState.forceSelection(
          editorWithInsert,
          textWithInsert.getSelectionAfter()
        );
        setEditorState(newEditorState);
      }
    },
    [editorState, setEditorState]
  );

  const myKeyBindingFn = useCallback(
    (e) => {
      if (e.keyCode === 9 /* Tab key */) {
        handleTab(e);
      }
      return getDefaultKeyBinding(e);
    },
    [handleTab]
  );

  return { handleTab, myKeyBindingFn };
};

// Reproduit l'override de permission du selector de Navbar.jsx (box img/variable
// dans un container non-editable d'une proposal live => masqué des actions).
// Existe à part pour rester appelable hors du composant Navbar (barre flottante box).
export const useSelectedBoxWithPermission = () => {
  const { selectedBoxId, boxes, masks, containers, currentProposal } = useStore(
    ({ selectedBoxId, boxes, masks, containers, currentProposal }) => ({
      selectedBoxId,
      boxes,
      masks,
      containers,
      currentProposal,
    })
  );
  const box = [...boxes, ...masks.map((m) => m.boxes).flat()].find(
    (box) => selectedBoxId && box.id === selectedBoxId
  );
  const container = containers.find((container) =>
    container.columnsIds.includes(box?.columnId)
  );
  if (
    !container?.editable &&
    (isAImgBox(box?.type) || isAVariableBox(box?.type)) &&
    container?.hasOwnProperty('editable') &&
    currentProposal.id !== null
  ) {
    return {};
  }
  return box;
};

// Source unique pour copier/supprimer une box ou un container, partagée entre
// le panel de droite et la barre flottante du container. Par défaut la cible
// suit la sélection : cliquer une box sélectionne la box ET son container (cf.
// `fromBoxClick` dans Container.jsx), et c'est alors la box qui l'emporte ;
// cliquer le fond du container désélectionne la box, et les actions visent le
// container. C'est ce comportement par défaut que le panel de droite utilise.
// `containerOnly` force le container quelle que soit la sélection : c'est le
// mode de la barre flottante du container, dont les boutons doivent toujours
// viser le container qui la porte.
export const useBoxContainerActions = (selectedBoxArg, { containerOnly = false } = {}) => {
  const { t } = useTranslation();
  const { modal: modalApi } = App.useApp();
  // useContext et non useOverlay : ce hook peut tourner hors du Layout (rendu
  // PDF), où useOverlay lève. La valeur par défaut du contexte rend des no-op.
  const { showLeftPanel } = useContext(OverlayContext);
  const {
    currentDocument,
    removeBox,
    addCopy,
    boxVariableSelectedId,
    selectedContainer,
    selectedContainerIds,
    removeSelectedContainers,
    removeContainer,
    removeSectionContainers,
    removePdfImportContainers,
    configuration,
    containers,
    boxes,
    columns,
    rowContainers,
    groupSelection: groupSelectionState,
  } = useStore((state) => ({
    currentDocument: state.currentDocument,
    removeBox: state.removeBox,
    addCopy: state.addCopy,
    boxVariableSelectedId: state.boxVariableSelectedId,
    selectedContainer: state.selectedContainer,
    selectedContainerIds: state.selectedContainerIds,
    removeSelectedContainers: state.removeSelectedContainers,
    removeContainer: state.removeContainer,
    removeSectionContainers: state.removeSectionContainers,
    removePdfImportContainers: state.removePdfImportContainers,
    configuration: state.configuration,
    containers: state.containers,
    boxes: state.boxes,
    columns: state.columns,
    rowContainers: state.rowContainers,
    groupSelection: state.groupSelection,
  }));

  // En mode container, on masque toute sélection de box au reste du hook : le
  // store garde bien la box sélectionnée (le panel de droite en a besoin), mais
  // copier/supprimer ne doivent voir que le container.
  const selectedBox = containerOnly ? null : selectedBoxArg;
  const groupSelection = containerOnly ? [] : groupSelectionState;

  const isSelectionBox = isASelectionBox(selectedBox?.type);
  // Supprimer un container est réservé au modèle (ou à une proposition
  // déverrouillée) : même règle que canDeleteContainers plus bas.
  const canDeleteBlocks =
    currentDocument.type === 'template' || configuration.unlockProposal;
  // Un espace ne se pose que seul, dans un container à une colonne : pour
  // l'utilisateur il *est* le bloc, comme une box d'injection. La corbeille ne
  // peut donc rien faire d'utile sans le droit de supprimer le container — le
  // retirer seul laissait un container vide à supprimer dans un deuxième temps.
  const isLoneSpaceBox = selectedBox?.type === ItemTypes.SPACE;

  const deleteButton =
    selectedBox && !boxVariableSelectedId &&
    (selectedBox.type === 'text' ||
      selectedBox.type === 'table' ||
      selectedBox.type === 'img' ||
      selectedBox.type === 'shape' ||
      selectedBox.type === 'line' ||
      selectedBox.type === 'title1' ||
      selectedBox.type === 'title2' ||
      selectedBox.type === 'title3' ||
      selectedBox.type === 'title4' ||
      selectedBox.type === 'summary' ||
      selectedBox.type === 'line_break' ||
      selectedBox.type === ItemTypes.CHECKBOXS_VARIABLE ||
      selectedBox.type === ItemTypes.SELECTOR_VARIABLE ||
      selectedBox.type === ItemTypes.TEXT_VARIABLE ||
      selectedBox.type === ItemTypes.IMG_VARIABLE ||
      selectedBox.type === ItemTypes.TABLE_VARIABLE ||
      selectedBox.type === ItemTypes.GROUP_BOXES ||
      selectedBox.type === ItemTypes.ORG_CHART ||
      selectedBox.type === ItemTypes.INFO_CARD ||
      selectedBox.type === ItemTypes.ACCORDION_PANEL ||
      (isLoneSpaceBox && canDeleteBlocks));
  const hasSelectedContainers = (selectedContainerIds?.length || 0) > 0;
  const canDeleteContainers =
    hasSelectedContainers &&
    (currentDocument.type === 'template' || configuration.unlockProposal);
  const shouldDeleteContainers = canDeleteContainers && hasSelectedContainers;

  const isCopyDisabled = () => {
    if (isSelectionBox) return true;
    const canCopied = boxes
      ?.filter(({ columnId }) => selectedContainer?.columnsIds.includes(columnId))
      ?.some((box) => box.type === ItemTypes.SECTION || box.type === ItemTypes.RECURSIVE_SECTION || box.type === ItemTypes.RECURSIVE_MODEL || box.type === ItemTypes.MASK || box.type === ItemTypes.FILE_PDF);
    if (selectedBox) return false;
    if (!canCopied && selectedContainer) return false;
    if (canCopied) return true;
    if (!selectedContainer) return true;
  };

  const copySelection = () => {
    if (selectedBox) CopyBox(selectedBox, addCopy);
    else if (selectedContainer) CopyContainer({ boxes, columns, rowContainers, selectedContainer, addCopy });
    else return;
    // Le presse-papiers vit dans le panneau « Éléments » du rail gauche : sans
    // l'ouvrir, copier ne donne aucun retour visible, et la vignette ajoutée
    // reste invisible tant qu'on ne va pas la chercher. showLeftPanel et non
    // updateLeftShownStatus : ce dernier désélectionne la box en modèle.
    showLeftPanel?.(leftShownStatus.ELEMENTS);
  };

  const deleteSelection = () => {
    // Descend dans les lignes imbriquées : un container découpé en lignes ne
    // porte aucune box sur ses colonnes de premier niveau.
    const getContainerBox = (container) => {
      const columnIds = new Set(
        getContainerColumnIds({ container, columns, rowContainers })
      );
      return boxes.find((b) => columnIds.has(b.columnId));
    };
    const selectedBoxContainer = selectedBox?.id
      ? containers.find((c) => c.columnsIds?.includes(selectedBox.columnId))
      : null;
    // Une box posée dans une ligne imbriquée n'appartient à aucune colonne du
    // container : son bloc se retrouve en remontant la chaîne des lignes.
    const selectedBoxBlock = selectedBox?.id
      ? selectedBoxContainer ??
        getTopLevelContainer({
          columnId: selectedBox.columnId,
          containers,
          rowContainers,
        })
      : null;
    const countBoxesInContainer = (container) => {
      const columnIds = new Set(
        getContainerColumnIds({ container, columns, rowContainers })
      );
      return boxes.filter((b) => columnIds.has(b.columnId)).length;
    };

    const isDrawModeContainer = selectedBoxContainer?.columnsIds?.some((colId) =>
      columns.find((col) => col.id === colId)?.drawMode
    );

    const boxesInGroupSelection = boxes.filter((b) =>
      groupSelection?.includes(b.id)
    );

    const hasDrawModeInGroupSelection = boxesInGroupSelection.some((box) => {
      const column = columns.find((col) => col.id === box.columnId);
      return column?.drawMode;
    });

    const isDrawModeContext = isDrawModeContainer || hasDrawModeInGroupSelection;
    const openSectionDeleteConfirm = ({ container, boxInContainer }) => {
      const modalInst = modalApi.confirm({
        title: t('container-deleteWarning'),
        cancelText: t('cancel'),
        okText: t('delete'),
        okType: 'danger',
        onOk: () => {
          removeContainer(container.id);
        },
        footer: (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', gap: 10 }}>
            <Button onClick={() => modalInst.destroy()}>{t('cancel')}</Button>
            <Button
              danger
              onClick={() => {
                removeSectionContainers(
                  boxInContainer?.fromBoxId || container?.fromBoxId,
                  { sectionId: container?.sectionId, dateTimestamp: container?.dateTimestamp }
                );
                modalInst.destroy();
              }}
            >
              {t('section-delete-all-container')}
            </Button>
            <Button
              danger
              disabled={container?.from_linked_section}
              onClick={() => {
                removeContainer(container.id);
                modalInst.destroy();
              }}
            >
              {t('delete')}
            </Button>
          </div>
        ),
      });
    };

    // Page d'un PDF importé : un mauvais PDF de 50 pages ne doit pas se
    // retirer page par page, on propose aussi de supprimer toutes ses pages.
    const openPdfImportDeleteConfirm = ({ container }) => {
      const { id: containerId, KeyPdfToImage } = container;
      const modalInst = modalApi.confirm({
        title: t('delete-block-confirm'),
        cancelText: t('cancel'),
        okText: t('delete'),
        okType: 'danger',
        onOk: () => removeContainer(containerId),
        footer: (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', gap: 10 }}>
            <Button onClick={() => modalInst.destroy()}>{t('cancel')}</Button>
            <Button
              danger
              onClick={() => {
                removePdfImportContainers(KeyPdfToImage);
                modalInst.destroy();
              }}
            >
              {t('pdf-to-image-delete-all-container')}
            </Button>
            <Button
              danger
              onClick={() => {
                removeContainer(containerId);
                modalInst.destroy();
              }}
            >
              {t('delete')}
            </Button>
          </div>
        ),
      });
    };

    const hasAnyBoxSelection = !!selectedBox?.id || (groupSelection?.length || 0) > 0;

    // Un tableau hors mode dessin est dans le même cas : sa colonne ne porte
    // que lui (hors dessin, un drop sur colonne occupée remplace la box au lieu
    // de s'empiler, cf. ColumnWrapper) et un container qui porte un tableau
    // n'a qu'une colonne — le tableau *est* le bloc. En mode dessin au
    // contraire les box cohabitent librement dans la colonne : on laisse la
    // branche suivante ne supprimer que la box.
    const isLoneTableBox = isATableBox(selectedBox?.type) && !isDrawModeContext;

    // Container qui ne porte qu'une box, où qu'elle soit posée (colonne ou
    // ligne imbriquée) : elle *est* le bloc. Supprimer l'une puis l'autre
    // demandait deux passages et laissait un container vide entre les deux —
    // et le résultat doit être le même qu'on ait sélectionné la box ou le
    // container. Une diapositive est exclue : ses box cohabitent librement, y
    // supprimer la dernière ne doit pas emporter la page.
    const isLoneBoxInContainer =
      !!selectedBoxBlock &&
      !isDrawModeContext &&
      countBoxesInContainer(selectedBoxBlock) === 1;

    // Container à supprimer quand la box vaut pour le bloc entier. Une box
    // imbriquée dans une ligne ne compte que si elle est seule dans tout le
    // container : sinon supprimer un tableau posé dans une ligne emporterait
    // les autres lignes avec lui.
    const blockContainer = isLoneBoxInContainer
      ? selectedBoxBlock
      : selectedBoxContainer;

    // Box d'injection (section, PDF, masque, modèle récursif) : elle occupe
    // seule son container, et pour l'utilisateur elle *est* le bloc. La
    // supprimer seule laissait un container vide à retirer ensuite.
    //
    // À placer avant la branche « mode dessin » : ces box sont posées dans un
    // container drawMode (cf. isADrawBox), qui sinon l'emporte et ne propose
    // que la suppression de la box. Les box librement posées d'une diapositive
    // ne sont pas des box de sélection, elles gardent leur traitement.
    if (
      (isSelectionBox || isLoneTableBox || isLoneSpaceBox || isLoneBoxInContainer) &&
      blockContainer &&
      (groupSelection?.length || 0) === 0 &&
      canDeleteBlocks
    ) {
      // Container issu d'une section injectée : le choix « ce bloc » ou « toute
      // la section » garde tout son sens, c'est la boîte de dialogue dédiée.
      if (blockContainer.sectionId) {
        openSectionDeleteConfirm({
          container: blockContainer,
          boxInContainer: selectedBox,
        });
        return;
      }
      if (blockContainer.KeyPdfToImage) {
        openPdfImportDeleteConfirm({ container: blockContainer });
        return;
      }
      const containerIdSnapshot = blockContainer.id;
      modalApi.confirm({
        title: t('delete-block-confirm'),
        okText: t('yes'),
        cancelText: t('cancel'),
        okType: 'danger',
        onOk: () => removeContainer(containerIdSnapshot),
      });
      return;
    }

    // L'image seule d'une page PDF (mise en page avec en-tête et pied de page,
    // container en mode dessin) est la page : la retirer seule laissait une
    // page vide, et la branche suivante ne propose que la box.
    if (
      selectedBoxContainer?.KeyPdfToImage &&
      (groupSelection?.length || 0) === 0 &&
      canDeleteBlocks &&
      countBoxesInContainer(selectedBoxContainer) === 1
    ) {
      openPdfImportDeleteConfirm({ container: selectedBoxContainer });
      return;
    }

    if (isDrawModeContext && hasAnyBoxSelection) {
      const boxIdsToDelete = Array.from(
        new Set([
          ...(selectedBox?.id ? [selectedBox.id] : []),
          ...(groupSelection || []),
        ])
      );

      if (boxIdsToDelete.length === 0) {
        return;
      }

      modalApi.confirm({
        title: GLOSSARY.REMOVE_BOX({ count: boxIdsToDelete.length }),
        okText: t('yes'),
        cancelText: t('cancel'),
        okType: 'danger',
        onOk: () => {
          boxIdsToDelete.forEach((id) => removeBox(id));
        },
      });
      return;
    }

    if (!hasAnyBoxSelection && shouldDeleteContainers) {
      const containerIdsSnapshot = [...selectedContainerIds];
      if (containerIdsSnapshot.length === 1) {
        const container = containers.find((c) => c.id === containerIdsSnapshot[0]);
        const boxInContainer = getContainerBox(container);
        if (container?.sectionId && boxInContainer) {
          openSectionDeleteConfirm({ container, boxInContainer });
          return;
        }
        if (container?.KeyPdfToImage) {
          openPdfImportDeleteConfirm({ container });
          return;
        }
      }
      modalApi.confirm({
        title:
          containerIdsSnapshot.length > 1
            ? t('delete-blocks-confirm', { count: containerIdsSnapshot.length })
            : t('delete-block-confirm'),
        okText: t('yes'),
        cancelText: t('cancel'),
        okType: 'danger',
        onOk: () => removeSelectedContainers(containerIdsSnapshot),
      });
      return;
    }
    if (selectedBox?.id) {
      if (selectedBoxContainer?.sectionId) {
        openSectionDeleteConfirm({
          container: selectedBoxContainer,
          boxInContainer: selectedBox,
        });
        return;
      }
      const boxIdSnapshot = selectedBox.id;
      modalApi.confirm({
        title: GLOSSARY.REMOVE_BOX({ count: 1 }),
        okText: t('yes'),
        cancelText: t('cancel'),
        okType: 'danger',
        onOk: () => removeBox(boxIdSnapshot),
      });
    }
  };

  return {
    deleteButton,
    canDeleteContainers,
    isCopyDisabled: isCopyDisabled(),
    isDeleteDisabled: !deleteButton && !canDeleteContainers,
    copySelection,
    deleteSelection,
  };
};

// Désignation du document ouvert pour les messages des panneaux vides.
export const useDocumentKindLabel = () =>
  useStore(({ currentDocument, currentTemplate }) => {
    if (currentDocument.type !== 'template') return 'le document';
    return currentTemplate?.pattern === 'section' ? 'la section' : 'le modèle';
  });
